import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Mail,
  Download,
  Tag,
  ExternalLink,
  BarChart3,
  Clock,
  User,
  Star,
  Sparkles,
  ArrowRight,
  X,
  Activity,
  Calendar,
  TrendingUp,
  Smartphone,
  Monitor,
  CreditCard,
  FileText,
  Target,
  Zap,
  Globe,
  Smartphone as Mobile,
  Monitor as Desktop,
  DollarSign,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { Progress } from '../../components/ui/progress';

const UserDetailPanel = ({ user, isOpen, onClose }) => {
  const [sections, setSections] = useState({
    overview: true,
    aiFeatures: false,
    journey: false,
    activity: false,
    actions: false
  });

  const toggleSection = (section) => {
    setSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getValueScoreColor = (score) => {
    if (!score || score === 0) return 'text-gray-500';
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getJourneyStageColor = (stage) => {
    if (!stage) return 'bg-gray-500/10 text-gray-500 border-gray-500/20';

    switch (stage) {
      case 'paid':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'saved':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'started':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'engaged':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'new':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default:
        return 'bg-red-500/10 text-red-500 border-red-500/20';
    }
  };

  const getJourneyStageIcon = (stage) => {
    if (!stage) return <User className="h-3 w-3" />;

    switch (stage) {
      case 'paid':
        return <DollarSign className="h-3 w-3" />;
      case 'saved':
        return <FileText className="h-3 w-3" />;
      case 'started':
        return <BarChart3 className="h-3 w-3" />;
      case 'engaged':
        return <Activity className="h-3 w-3" />;
      case 'new':
        return <User className="h-3 w-3" />;
      default:
        return <User className="h-3 w-3" />;
    }
  };

  const calculateFunnelProgress = (stage) => {
    if (!stage) return 0;

    const stages = ['new', 'engaged', 'started', 'saved', 'paid'];
    const currentIndex = stages.indexOf(stage);
    return ((currentIndex + 1) / stages.length) * 100;
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const now = new Date();
    const activityDate = timestamp?.toDate?.() || new Date(timestamp);
    const diffInMs = now - activityDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months}mo ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years}y ago`;
    }
  };

  // Calculate real analytics-based metrics
  const getAnalyticsMetrics = () => {
    if (!user) {
      return {
        totalEvents: 0,
        aiEvents: 0,
        downloads: 0,
        saves: 0,
        resumes: 0,
        aiSuggestionViewed: 0,
        aiSuggestionApplied: 0,
        aiAutofill: 0,
        aiOptimizeTriggered: 0,
        aiOptimizeApplied: 0
      };
    }

    return {
      totalEvents: user.totalEvents || 0,
      aiEvents: user.aiEventsCount || 0,
      downloads: user.downloadEventsCount || 0,
      saves: user.saveEventsCount || 0,
      resumes: user.totalResumes || 0,
      aiSuggestionViewed: user.aiSuggestionViewedCount || 0,
      aiSuggestionApplied: user.aiSuggestionAppliedCount || 0,
      aiAutofill: user.aiAutofillTriggeredCount || 0,
      aiOptimizeTriggered: user.aiOptimizeTriggeredCount || 0,
      aiOptimizeApplied: user.aiOptimizeAppliedCount || 0
    };
  };

  const metrics = getAnalyticsMetrics();

  if (!user) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          side="right"
          className="w-full sm:w-[400px] md:w-[540px] bg-background/95 backdrop-blur-xl border-l border-border p-0"
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No user selected</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:w-[400px] md:w-[540px] bg-background/95 backdrop-blur-xl border-l border-border p-0"
      >
        <ScrollArea className="h-full">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Header */}
            <SheetHeader className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <SheetTitle className="text-lg sm:text-xl font-semibold text-foreground/90 truncate">
                      {user.displayName || user.name || 'Unknown User'}
                    </SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground truncate">
                      {user.email}
                    </SheetDescription>
                  </div>
                </div>
                <SheetClose className="rounded-full p-2 hover:bg-muted flex-shrink-0">
                  <X className="h-4 w-4" />
                </SheetClose>
              </div>
            </SheetHeader>

            {/* Overview Section */}
            <Collapsible
              open={sections.overview}
              onOpenChange={() => toggleSection('overview')}
              className="space-y-2"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">Overview</h3>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  sections.overview ? "transform rotate-180" : ""
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                {/* Key Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Value Score</span>
                    </div>
                    <div className={cn(
                      "text-lg font-semibold",
                      getValueScoreColor(user.valueScore || 0)
                    )}>
                      {user.valueScore || 0}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <div className="flex items-center gap-2 mb-1">
                      {getJourneyStageIcon(user.journeyStage)}
                      <span className="text-xs text-muted-foreground">Journey</span>
                    </div>
                    <Badge className={cn("border", getJourneyStageColor(user.journeyStage || 'new'))}>
                      {user.journeyStage || 'new'}
                    </Badge>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      Journey Progress
                    </span>
                    <span className="text-muted-foreground">{Math.round(calculateFunnelProgress(user.journeyStage))}%</span>
                  </div>
                  <Progress value={calculateFunnelProgress(user.journeyStage)} className="h-2" />
                </div>

                {/* Tags */}
                {user.tags && user.tags.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Tag className="h-3 w-3" />
                      Tags
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {user.tags.slice(0, 4).map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs border-border">
                          {tag}
                        </Badge>
                      ))}
                      {user.tags.length > 4 && (
                        <Badge variant="outline" className="text-xs border-border">
                          +{user.tags.length - 4}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Subscription Status */}
                <div className="p-3 rounded-lg bg-card border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <CreditCard className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Subscription</span>
                    </div>
                    <Badge className={user.isSubscribed ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-gray-500/10 text-gray-500 border-gray-500/20"}>
                      {user.isSubscribed ? (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Inactive
                        </div>
                      )}
                    </Badge>
                  </div>
                  {user.isSubscribed && (
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plan:</span>
                        <span>{user.subscription?.plan || 'Premium'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Credits:</span>
                        <span>{user.downloadCredits || 0}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Timeline */}
                <div className="space-y-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Activity className="h-3 w-3" />
                    Recent Activity
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between items-center p-2 rounded-lg bg-card border border-border">
                      <span>Last Activity</span>
                      <span className="font-medium">
                        {formatRelativeTime(user.lastActivity || user.lastActivityTimestamp || user.createdAt)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded-lg bg-card border border-border">
                      <span>Member Since</span>
                      <span className="font-medium">
                        {user.createdAt ?
                          new Date(user.createdAt.toDate?.() || user.createdAt).toLocaleDateString() :
                          'Unknown'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* AI Features */}
            <Collapsible
              open={sections.aiFeatures}
              onOpenChange={() => toggleSection('aiFeatures')}
              className="space-y-2"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">AI Features</h3>
                  {metrics.aiEvents > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs bg-primary/10 text-primary">
                      {metrics.aiEvents}
                    </Badge>
                  )}
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  sections.aiFeatures ? "transform rotate-180" : ""
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-2">
                {(user.aiFeaturesUsed || []).length > 0 ? (
                  <div className="space-y-2">
                    {user.aiFeaturesUsed.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-card border border-border">
                        <span className="text-sm">{feature}</span>
                        <Badge variant="secondary" className="bg-primary/10 text-primary text-xs border-primary/20">
                          Used
                        </Badge>
                      </div>
                    ))}
                    {user.aiFeaturesUsed.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{user.aiFeaturesUsed.length - 3} more features
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-card border border-border text-center">
                    <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <span className="text-sm text-muted-foreground">No AI features used yet</span>
                  </div>
                )}

                {/* AI Usage Summary */}
                {metrics.aiEvents > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                    <div className="text-center p-2 rounded-lg bg-card border border-border">
                      <div className="text-lg font-semibold text-primary">{metrics.aiSuggestionViewed}</div>
                      <div className="text-xs text-muted-foreground">Viewed</div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-card border border-border">
                      <div className="text-lg font-semibold text-emerald-500">{metrics.aiSuggestionApplied}</div>
                      <div className="text-xs text-muted-foreground">Applied</div>
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* User Journey */}
            <Collapsible
              open={sections.journey}
              onOpenChange={() => toggleSection('journey')}
              className="space-y-2"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">Journey Stages</h3>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  sections.journey ? "transform rotate-180" : ""
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                <div className="space-y-2">
                  {['new', 'engaged', 'started', 'saved', 'paid'].map((stage, index) => (
                    <div
                      key={stage}
                      className={cn(
                        "flex items-center gap-3 p-2 rounded-lg transition-colors border",
                        user.journeyStage === stage
                          ? "bg-primary/10 border-primary/20"
                          : "bg-card border-border"
                      )}
                    >
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        user.journeyStage === stage ? "bg-primary" : "bg-muted-foreground/30"
                      )} />
                      <span className="text-sm capitalize flex-1">{stage}</span>
                      {user.journeyStage === stage && (
                        <Badge variant="secondary" className="bg-primary/10 text-primary text-xs border-primary/20">
                          Current
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Activity & Analytics */}
            <Collapsible
              open={sections.activity}
              onOpenChange={() => toggleSection('activity')}
              className="space-y-2"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">Activity</h3>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  sections.activity ? "transform rotate-180" : ""
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-2">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center p-2 rounded-lg bg-card border border-border">
                    <div className="text-lg font-semibold text-primary">{metrics.resumes}</div>
                    <div className="text-xs text-muted-foreground">Resumes</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card border border-border">
                    <div className="text-lg font-semibold text-emerald-500">{metrics.downloads}</div>
                    <div className="text-xs text-muted-foreground">Downloads</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card border border-border">
                    <div className="text-lg font-semibold text-blue-500">{metrics.totalEvents}</div>
                    <div className="text-xs text-muted-foreground">Total Events</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-card border border-border">
                    <div className="text-lg font-semibold text-purple-500">{metrics.aiEvents}</div>
                    <div className="text-xs text-muted-foreground">AI Events</div>
                  </div>
                </div>

                {/* Device Info */}
                {user.device && Object.keys(user.device).length > 0 && (
                  <div className="p-3 rounded-lg bg-card border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      {user.device.device === 'mobile' ? (
                        <Mobile className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <Desktop className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="text-xs text-muted-foreground">Device</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      {user.device.browser && (
                        <div className="flex justify-between">
                          <span>Browser:</span>
                          <span>{user.device.browser}</span>
                        </div>
                      )}
                      {user.device.os && (
                        <div className="flex justify-between">
                          <span>OS:</span>
                          <span>{user.device.os}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Actions */}
            <Collapsible
              open={sections.actions}
              onOpenChange={() => toggleSection('actions')}
              className="space-y-2"
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-card border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-medium">Actions</h3>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  sections.actions ? "transform rotate-180" : ""
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-card hover:bg-muted/50 border-border justify-start"
                    onClick={() => console.log('Send email to:', user.email)}
                  >
                    <Mail className="h-4 w-4" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-card hover:bg-muted/50 border-border justify-start"
                    onClick={() => console.log('View resume for:', user.id)}
                  >
                    <FileText className="h-4 w-4" />
                    View Resume
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-card hover:bg-muted/50 border-border justify-start"
                    onClick={() => console.log('Export data for:', user.id)}
                  >
                    <Download className="h-4 w-4" />
                    Export Data
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-card hover:bg-muted/50 border-border justify-start"
                    onClick={() => console.log('Manage tags for:', user.id)}
                  >
                    <Tag className="h-4 w-4" />
                    Manage Tags
                  </Button>
                  {!user.isSubscribed && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 bg-card hover:bg-muted/50 border-border justify-start sm:col-span-2"
                      onClick={() => console.log('Send discount to:', user.email)}
                    >
                      <Mail className="h-4 w-4" />
                      Send Discount Offer
                    </Button>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default UserDetailPanel; 